import { useFormik } from "formik";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Col,
  Form,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from "reactstrap";
import * as Yup from "yup";

import {
  CreateLiveLectureRequest,
  LiveLectureDto,
  ParsedError,
} from "../../interfaces";
import {
  useAddLiveLectureMutation,
  useDeleteLiveLectureMutation,
  useGetLiveLectureQuery,
  usePublishLiveLectureMutation,
  useUpdateLiveLectureMutation,
} from "../../store/api/liveLectureApi";
import { ActivityButton, AppRichEditor } from "../../components/Common";
import DeleteModal from "../../components/Common/DeleteModal";
import { applyServerFormErrors } from "../../services";
import {
  ErrorHandlingTypeEnum,
  LiveLectureAccessModeEnum,
  LiveLectureStatusEnum,
  LiveLectureTypeEnum,
  RoleEnum,
} from "../../enums";
import { AccountState } from "../../store/slices/accountSlice";
import { MediaDto } from "../../interfaces/media";
import { LiveLectureGallery } from "./LiveLectureGallery";
import { LiveLectureNotes } from "./LiveLectureNotes";
import { LiveLectureBookingsModal } from "./LiveLectureBookingsModal";
import {
  SelectedScheduleSlot,
  TeacherScheduleSlotPicker,
} from "./TeacherScheduleSlotPicker";

type TProps = {
  id?: string | null;
  preset?: Partial<LiveLectureDto>;
  show: boolean;
  onCloseClick: () => void;
  accessMode: LiveLectureAccessModeEnum;
  subjectId?: string;
  gradeId?: string;
  courseId?: string;
  courseDurationId?: string;
};

export const AddEditLiveLectureModal: React.FC<TProps> = ({
  id,
  preset,
  show,
  onCloseClick,
  accessMode,
  subjectId,
  gradeId,
  courseId,
  courseDurationId,
}) => {
  const { t, i18n } = useTranslation();
  const profile = useSelector(
    (state: { account: AccountState }) => state.account.userProfile,
  );
  const isTeacherAdmin = profile?.role?.key === RoleEnum.TEACHER_ADMIN;
  const isEdit = Boolean(id);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showBookingsModal, setShowBookingsModal] = useState(false);
  const [teacherOption, setTeacherOption] = useState<any>(null);
  const [selectedSlot, setSelectedSlot] = useState<SelectedScheduleSlot | null>(
    null,
  );
  const [slotError, setSlotError] = useState<string | null>(null);
  const [pendingMedia, setPendingMedia] = useState<MediaDto[]>([]);
  const [pendingNotes, setPendingNotes] = useState<MediaDto[]>([]);

  const { data: liveLectureData } = useGetLiveLectureQuery(id as string, {
    skip: !id,
  });

  const liveLecture = liveLectureData || preset;
  const effectiveAccessMode =
    liveLectureData?.accessMode ?? preset?.accessMode ?? accessMode;
  const isSubjectIncluded =
    effectiveAccessMode === LiveLectureAccessModeEnum.SUBJECT_INCLUDED;
  const lockTeacher = isTeacherAdmin && isSubjectIncluded;

  const [
    addLiveLecture,
    { isLoading: isLoadingAdd, error: errorAdd, isError: isErrorAdd },
  ] = useAddLiveLectureMutation();
  const [
    updateLiveLecture,
    { isLoading: isLoadingUpdate, error: errorUpdate, isError: isErrorUpdate },
  ] = useUpdateLiveLectureMutation();
  const [
    deleteLiveLecture,
    { isLoading: isLoadingDelete, error: errorDelete, isError: isErrorDelete },
  ] = useDeleteLiveLectureMutation();
  const [publishLiveLecture, { isLoading: isLoadingPublish }] =
    usePublishLiveLectureMutation();

  const initialValues = useMemo<CreateLiveLectureRequest>(
    () => ({
      title: liveLecture?.title || { en: "", ar: "" },
      description: liveLecture?.description || { en: "", ar: "" },
      type: liveLecture?.type || LiveLectureTypeEnum.GROUP,
      accessMode: effectiveAccessMode,
      subjectId: subjectId || liveLecture?.subjectId,
      teacherUserId: liveLecture?.teacherUserId || "",
      startAt: liveLecture?.startAt
        ? new Date(liveLecture.startAt)
        : new Date(),
      endAt: liveLecture?.endAt ? new Date(liveLecture.endAt) : new Date(),
      durationMinutes: liveLecture?.durationMinutes || 60,
      ...(isSubjectIncluded
        ? {}
        : { maxParticipants: liveLecture?.maxParticipants || 5 }),
      price: isSubjectIncluded ? 0 : Number(liveLecture?.price ?? 0),
      currency: liveLecture?.currency || "KWD",
    }),
    [liveLecture, effectiveAccessMode, subjectId, isSubjectIncluded],
  );

  const originalStartAtMs = useMemo(
    () =>
      liveLectureData?.startAt
        ? new Date(liveLectureData.startAt).getTime()
        : undefined,
    [liveLectureData?.startAt],
  );

  const initialSlot = useMemo<SelectedScheduleSlot | null>(() => {
    if (!liveLectureData?.startAt || !liveLectureData?.endAt) {
      return null;
    }
    return {
      startAt: new Date(liveLectureData.startAt),
      endAt: new Date(liveLectureData.endAt),
      durationMinutes: liveLectureData.durationMinutes,
    };
  }, [liveLectureData]);

  const validateSelectedSlot = useCallback(
    (slot: SelectedScheduleSlot | null): string | null => {
      if (!slot) {
        return t("isNotEmpty", { displayName: "availableSlots" });
      }

      const now = new Date();
      const unchanged =
        isEdit &&
        originalStartAtMs !== undefined &&
        Math.abs(slot.startAt.getTime() - originalStartAtMs) < 60000;

      const isDraft =
        liveLectureData?.status === LiveLectureStatusEnum.DRAFT;

      if (!isDraft && !unchanged && slot.startAt < now) {
        return t("liveLectureStartInPast");
      }

      if (slot.endAt <= slot.startAt) {
        return t("endTimeMustBeAfterStartTime");
      }

      return null;
    },
    [isEdit, liveLectureData?.status, originalStartAtMs, t],
  );

  const syncedLectureIdRef = useRef<string | null>(null);
  const modalWasOpenRef = useRef(false);

  const form = useFormik<CreateLiveLectureRequest>({
    enableReinitialize: true,
    initialValues,
    validationSchema: Yup.object({
      title: Yup.object({
        en: Yup.string().required(
          t("isNotEmpty", { displayName: "englishValue" }),
        ),
        ar: Yup.string().required(
          t("isNotEmpty", { displayName: "arabicValue" }),
        ),
      }),
      type: isSubjectIncluded
        ? Yup.string().optional()
        : Yup.string().required(
            t("isNotEmpty", { displayName: "liveLectureType" }),
          ),
      ...(isSubjectIncluded ? {} : { maxParticipants: Yup.number().min(1) }),
      price: isSubjectIncluded
        ? Yup.number().min(0)
        : Yup.number().min(0, t("isNotEmpty", { displayName: "price" })),
    }),
    onSubmit: async (values) => {
      const teacherUserId = teacherOption?.value || values.teacherUserId;
      if (!teacherUserId) {
        form.setFieldError(
          "teacherUserId",
          t("isNotEmpty", { displayName: "teacher" }),
        );
        return;
      }

      const slotValidationError = validateSelectedSlot(selectedSlot);
      if (slotValidationError) {
        setSlotError(slotValidationError);
        return;
      }
      setSlotError(null);

      const startAt = selectedSlot!.startAt;
      const endAt = selectedSlot!.endAt;

      const payload = {
        ...values,
        teacherUserId,
        accessMode: effectiveAccessMode,
        subjectId: isSubjectIncluded ? subjectId : undefined,
        ...(isSubjectIncluded
          ? {
              gradeId,
              courseId,
              courseDurationId:
                courseDurationId || liveLecture?.courseDurationId,
            }
          : {}),
        startAt,
        endAt,
        durationMinutes: selectedSlot!.durationMinutes,
        price: isSubjectIncluded ? 0 : Number(values.price),
        ...(isSubjectIncluded
          ? { type: undefined, maxParticipants: undefined }
          : {}),
        ...(!isEdit && (isSubjectIncluded ? pendingNotes : pendingMedia).length
          ? {
              mediaIds: (isSubjectIncluded ? pendingNotes : pendingMedia).map(
                (item) => item.id,
              ),
            }
          : {}),
      };

      try {
        if (isEdit && id) {
          await updateLiveLecture({ id, data: payload }).unwrap();
        } else {
          await addLiveLecture(payload).unwrap();
        }
        onClose();
      } catch {
        // Errors are handled by the API error effect.
      }
    },
  });

  const resolvedTeacherUserId =
    teacherOption?.value || form.values.teacherUserId || "";

  const descriptionErrors = form.errors.description as
    | { en?: string; ar?: string }
    | undefined;
  const descriptionTouched = form.touched.description as
    | { en?: boolean; ar?: boolean }
    | undefined;

  const handleTeacherChange = useCallback(
    (teacherUserId: string, option: any) => {
      setTeacherOption(option);
      form.setFieldValue("teacherUserId", teacherUserId, true);
      if (teacherUserId) {
        form.setFieldError("teacherUserId", undefined);
      }
    },
    [form],
  );

  useEffect(() => {
    if (!show) {
      syncedLectureIdRef.current = null;
      modalWasOpenRef.current = false;
      return;
    }

    const justOpened = !modalWasOpenRef.current;
    modalWasOpenRef.current = true;

    if (id && liveLectureData) {
      if (syncedLectureIdRef.current === id) {
        return;
      }
      syncedLectureIdRef.current = id;

      const startAt = new Date(liveLectureData.startAt);
      const endAt = new Date(liveLectureData.endAt);

      setSelectedSlot({
        startAt,
        endAt,
        durationMinutes: liveLectureData.durationMinutes,
      });
      setSlotError(
        validateSelectedSlot({
          startAt,
          endAt,
          durationMinutes: liveLectureData.durationMinutes,
        }),
      );
      form.setValues({
        title: liveLectureData.title,
        description: liveLectureData.description || { en: "", ar: "" },
        ...(isSubjectIncluded ? {} : { type: liveLectureData.type }),
        accessMode: effectiveAccessMode,
        subjectId: subjectId || liveLectureData.subjectId,
        teacherUserId: liveLectureData.teacherUserId,
        startAt,
        endAt,
        durationMinutes: liveLectureData.durationMinutes,
        ...(isSubjectIncluded
          ? {}
          : { maxParticipants: liveLectureData.maxParticipants }),
        price: isSubjectIncluded ? 0 : Number(liveLectureData.price),
        currency: liveLectureData.currency || "KWD",
      });

      if (liveLectureData.teacher) {
        setTeacherOption({
          label:
            i18n.language === "ar"
              ? liveLectureData.teacher.name.ar
              : liveLectureData.teacher.name.en,
          value: liveLectureData.teacherUserId,
        });
      }
      return;
    }

    if (!id && justOpened) {
      syncedLectureIdRef.current = null;
      setTeacherOption(null);
      setSelectedSlot(null);
      setSlotError(null);
      setPendingMedia([]);
      setPendingNotes([]);

      if (lockTeacher && profile?.id) {
        const teacherLabel =
          i18n.language === "ar" ? profile.name?.ar : profile.name?.en;
        setTeacherOption({
          label: teacherLabel,
          value: profile.id,
        });
        form.setFieldValue("teacherUserId", profile.id);
      }
    }
  }, [
    show,
    id,
    liveLectureData,
    effectiveAccessMode,
    subjectId,
    isSubjectIncluded,
    i18n.language,
    lockTeacher,
    profile?.id,
    profile?.name?.ar,
    profile?.name?.en,
    validateSelectedSlot,
  ]);

  useEffect(() => {
    let _error: ParsedError | null = null;
    if (!isLoadingAdd && isErrorAdd) _error = errorAdd as ParsedError;
    if (!isLoadingUpdate && isErrorUpdate) _error = errorUpdate as ParsedError;
    if (!isLoadingDelete && isErrorDelete) _error = errorDelete as ParsedError;

    if (_error?.type === ErrorHandlingTypeEnum.FORM && _error.error) {
      applyServerFormErrors(form, _error.error);

      const slotMessage =
        (typeof _error.error.startAt === "string"
          ? _error.error.startAt
          : null) ||
        (typeof _error.error.endAt === "string" ? _error.error.endAt : null) ||
        (typeof _error.error.durationMinutes === "string"
          ? _error.error.durationMinutes
          : null);

      if (slotMessage) {
        setSlotError(slotMessage);
      }
    }
  }, [
    isLoadingAdd,
    isLoadingUpdate,
    isLoadingDelete,
    isErrorAdd,
    isErrorUpdate,
    isErrorDelete,
  ]);

  const onClose = () => {
    syncedLectureIdRef.current = null;
    form.resetForm();
    setTeacherOption(null);
    setSlotError(null);
    setSelectedSlot(null);
    setPendingMedia([]);
    setPendingNotes([]);
    onCloseClick();
  };

  const onDeleteConfirm = async () => {
    if (!id) return;
    await deleteLiveLecture(id);
    setShowDeleteModal(false);
    onClose();
  };

  const onPublish = async () => {
    if (!id) return;
    const slotValidationError = validateSelectedSlot(selectedSlot);
    if (slotValidationError) {
      setSlotError(slotValidationError);
      return;
    }
    await publishLiveLecture(id);
    onClose();
  };

  const canPublish =
    isEdit && liveLecture?.status === LiveLectureStatusEnum.DRAFT && id;

  return (
    <React.Fragment>
      <Modal
        id="addEditLiveLecture"
        isOpen={show}
        toggle={onClose}
        centered
        size="lg"
      >
        <Form
          onSubmit={async (e) => {
            e.preventDefault();
            const teacherUserId =
              teacherOption?.value || form.values.teacherUserId;
            await form.setFieldValue("teacherUserId", teacherUserId, false);
            form.handleSubmit();
            return false;
          }}
        >
          <ModalHeader className="p-3" toggle={onClose}>
            {isEdit
              ? t("editItem", { item: "liveLecture" })
              : t("addItem", { item: "liveLecture" })}
          </ModalHeader>
          <ModalBody style={{ maxHeight: "70vh", overflowY: "auto" }}>
            {!isSubjectIncluded && (
              <div className="mb-3">
                <Label className="form-label">
                  {t("liveLectureType")}
                  <span className="text-danger">*</span>
                </Label>
                <Input
                  type="select"
                  name="type"
                  value={form.values.type}
                  onChange={(event) => {
                    form.handleChange(event);
                    setSelectedSlot(null);
                  }}
                  onBlur={form.handleBlur}
                >
                  {Object.values(LiveLectureTypeEnum).map((type) => (
                    <option key={type} value={type}>
                      {t(`liveLectureType_${type}`)}
                    </option>
                  ))}
                </Input>
              </div>
            )}

            <TeacherScheduleSlotPicker
              subjectId={subjectId || liveLecture?.subjectId}
              gradeId={gradeId}
              courseId={courseId}
              courseDurationId={
                courseDurationId || liveLecture?.courseDurationId
              }
              lectureType={
                isSubjectIncluded
                  ? LiveLectureTypeEnum.GROUP
                  : form.values.type || LiveLectureTypeEnum.GROUP
              }
              teacherUserId={resolvedTeacherUserId}
              teacherOption={teacherOption}
              onTeacherChange={handleTeacherChange}
              selectedSlot={selectedSlot}
              onSlotChange={(slot) => {
                setSelectedSlot(slot);
                setSlotError(validateSelectedSlot(slot));
              }}
              slotError={slotError}
              initialSlot={initialSlot}
              lockTeacher={lockTeacher}
              excludeLiveLectureId={isEdit ? (id ?? undefined) : undefined}
              disabled={
                isLoadingAdd ||
                isLoadingUpdate ||
                isLoadingDelete ||
                isLoadingPublish
              }
            />

            {(form.submitCount > 0 || form.touched.teacherUserId) &&
            form.errors.teacherUserId ? (
              <FormFeedback type="invalid" className="d-block mb-3">
                {form.errors.teacherUserId}
              </FormFeedback>
            ) : null}

            {!isSubjectIncluded && (
              <Row>
                <Col md={6}>
                  <div className="mb-3">
                    <Label htmlFor="maxParticipants" className="form-label">
                      {t("maxParticipants")}
                    </Label>
                    <Input
                      id="maxParticipants"
                      name="maxParticipants"
                      type="number"
                      min={1}
                      value={form.values.maxParticipants}
                      onChange={form.handleChange}
                    />
                  </div>
                </Col>
                <Col md={6}>
                  <div className="mb-3">
                    <Label htmlFor="price" className="form-label">
                      {t("price")}
                      <span className="text-danger">*</span>
                    </Label>
                    <Input
                      id="price"
                      name="price"
                      type="number"
                      min={0}
                      step="0.01"
                      value={form.values.price}
                      onChange={form.handleChange}
                      invalid={
                        form.touched.price && form.errors.price ? true : false
                      }
                    />
                    {form.touched.price && form.errors.price ? (
                      <FormFeedback type="invalid">
                        {form.errors.price as string}
                      </FormFeedback>
                    ) : null}
                  </div>
                </Col>
              </Row>
            )}

            <div className="mb-3">
              <Label htmlFor="title.en" className="form-label">
                {t("titleEn")}
                <span className="text-danger">*</span>
              </Label>
              <Input
                id="title.en"
                name="title.en"
                value={form.values.title?.en}
                onChange={form.handleChange}
                onBlur={form.handleBlur}
                invalid={
                  form.touched.title?.en && form.errors.title?.en ? true : false
                }
              />
              {form.touched.title?.en && form.errors.title?.en ? (
                <FormFeedback type="invalid">
                  {form.errors.title?.en as string}
                </FormFeedback>
              ) : null}
            </div>

            <div className="mb-3">
              <Label htmlFor="title.ar" className="form-label">
                {t("titleAr")}
                <span className="text-danger">*</span>
              </Label>
              <Input
                style={{ fontFamily: "aljannat", direction: "rtl" }}
                id="title.ar"
                name="title.ar"
                value={form.values.title?.ar}
                onChange={form.handleChange}
                onBlur={form.handleBlur}
                invalid={
                  form.touched.title?.ar && form.errors.title?.ar ? true : false
                }
              />
              {form.touched.title?.ar && form.errors.title?.ar ? (
                <FormFeedback type="invalid">
                  {form.errors.title?.ar as string}
                </FormFeedback>
              ) : null}
            </div>

            {!isSubjectIncluded && (
              <LiveLectureGallery
                liveLectureId={id}
                media={liveLectureData?.media}
                pendingMedia={pendingMedia}
                onPendingMediaChange={setPendingMedia}
                disabled={
                  isLoadingAdd ||
                  isLoadingUpdate ||
                  isLoadingDelete ||
                  isLoadingPublish
                }
              />
            )}

            {isSubjectIncluded && (
              <LiveLectureNotes
                liveLectureId={id}
                media={liveLectureData?.media}
                pendingNotes={pendingNotes}
                onPendingNotesChange={setPendingNotes}
                disabled={
                  isLoadingAdd ||
                  isLoadingUpdate ||
                  isLoadingDelete ||
                  isLoadingPublish
                }
              />
            )}

            {isEdit && liveLectureData && !isSubjectIncluded && (
              <div className="mb-3 p-3 border rounded d-flex flex-wrap align-items-center justify-content-between gap-2">
                <div>
                  <span className="text-muted">{t("bookedCount")}: </span>
                  <strong>{liveLectureData.bookedCount}</strong>
                  {liveLectureData.maxParticipants ? (
                    <>
                      <span className="text-muted">/</span>
                      <strong>{liveLectureData.maxParticipants}</strong>
                      <span className="text-muted ms-2">
                        ({liveLectureData.availableSeats} {t("left")})
                      </span>
                    </>
                  ) : null}
                </div>
                <button
                  type="button"
                  className="btn btn-soft-info btn-sm"
                  onClick={() => setShowBookingsModal(true)}
                >
                  <i className="ri-group-line me-1"></i>
                  {t("viewBookings")}
                </button>
              </div>
            )}

            <div className="mb-3">
              <Label className="form-label">{t("descriptionEn")}</Label>
              <div
                className={
                  descriptionTouched?.en && descriptionErrors?.en
                    ? "rounded border border-danger"
                    : undefined
                }
              >
                <AppRichEditor
                  key={`desc-en-${id || "new"}`}
                  initialValue={form.values.description?.en || ""}
                  onChange={(data: string) => {
                    form.setFieldValue("description.en", data);
                    if (descriptionErrors?.en) {
                      form.setFieldError("description.en", undefined);
                    }
                  }}
                  onFocus={() => form.setFieldTouched("description.en", true)}
                />
              </div>
              {descriptionTouched?.en && descriptionErrors?.en ? (
                <FormFeedback type="invalid" className="d-block">
                  {descriptionErrors.en}
                </FormFeedback>
              ) : null}
            </div>

            <div className="mb-3">
              <Label className="form-label">{t("descriptionAr")}</Label>
              <div
                className={
                  descriptionTouched?.ar && descriptionErrors?.ar
                    ? "rounded border border-danger"
                    : undefined
                }
              >
                <AppRichEditor
                  key={`desc-ar-${id || "new"}`}
                  initialValue={form.values.description?.ar || ""}
                  onChange={(data: string) => {
                    form.setFieldValue("description.ar", data);
                    if (descriptionErrors?.ar) {
                      form.setFieldError("description.ar", undefined);
                    }
                  }}
                  onFocus={() => form.setFieldTouched("description.ar", true)}
                />
              </div>
              {descriptionTouched?.ar && descriptionErrors?.ar ? (
                <FormFeedback type="invalid" className="d-block">
                  {descriptionErrors.ar}
                </FormFeedback>
              ) : null}
            </div>
          </ModalBody>
          <ModalFooter>
            <Link
              to="#"
              onClick={onClose}
              className="btn btn-link link-success fw-medium"
            >
              <i className="ri-close-line me-1 align-middle"></i> {t("close")}
            </Link>
            {isEdit && (
              <ActivityButton
                onClick={() => setShowDeleteModal(true)}
                title="delete"
                inProgressTitle="deleting"
                color="danger"
                className="btn btn-danger"
                type="button"
                inProgress={isLoadingDelete}
                disabled={
                  isLoadingAdd ||
                  isLoadingUpdate ||
                  isLoadingDelete ||
                  isLoadingPublish
                }
              />
            )}
            {canPublish && (
              <ActivityButton
                onClick={onPublish}
                title="publishLiveLecture"
                inProgressTitle="publishing"
                color="primary"
                className="btn btn-primary"
                type="button"
                inProgress={isLoadingPublish}
                disabled={
                  isLoadingAdd ||
                  isLoadingUpdate ||
                  isLoadingDelete ||
                  isLoadingPublish
                }
              />
            )}
            <ActivityButton
              title="save"
              inProgressTitle="saving"
              color="success"
              className="btn btn-success"
              type="submit"
              inProgress={
                isLoadingAdd ||
                isLoadingUpdate ||
                isLoadingDelete ||
                isLoadingPublish
              }
              disabled={
                isLoadingAdd ||
                isLoadingUpdate ||
                isLoadingDelete ||
                isLoadingPublish
              }
            />
          </ModalFooter>
        </Form>
      </Modal>
      <DeleteModal
        loading={isLoadingDelete}
        show={showDeleteModal}
        onDeleteClick={onDeleteConfirm}
        onCloseClick={() => setShowDeleteModal(false)}
      />
      {!isSubjectIncluded && (
        <LiveLectureBookingsModal
          liveLectureId={id}
          show={showBookingsModal}
          onCloseClick={() => setShowBookingsModal(false)}
        />
      )}
    </React.Fragment>
  );
};
